import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	CategoriesService,
	type IGetCategoriesParams,
} from '@services/categories.service';

// --- Stores ---
import { CategoriesStore } from '@stores/categories.store';

// --- Interfaces ---
import type { ICategory } from '@interfaces/category';

const REFRESH_INTERVAL = 120 * 10000;

@Injectable({
	providedIn: 'root',
})
export class CategoriesFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);
	readonly refresh$ = this._refresh.asObservable();

	categories!: ICategory[];
	onlyCategories!: ICategory[];
	onlySubcategories!: ICategory[];

	private readonly _filter = new BehaviorSubject<IGetCategoriesParams>({});
	readonly filter$ = this._filter.asObservable().pipe(distinctUntilChanged());

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this.refresh$,
	]).pipe(
		switchMap(([filter]) => this.getCategories({ ...filter, pageLimit: 10 }))
	);

	readonly categoriesState$ = combineLatest([
		this.categoriesStore.categoriesState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => {
			this.categories = state.result;
			return state;
		}),
		shareReplay({ refCount: true })
	);

	constructor(
		private categoriesService: CategoriesService,
		private categoriesStore: CategoriesStore
	) {}

	getCategories(queryParams?: IGetCategoriesParams) {
		return this.categoriesService.getCategories(queryParams).pipe(
			tap(categories => {
				categories.result = this.formatDefaultCategoryName(categories.result);
				this.categoriesStore.updateCategories(categories);
			})
		);
	}

	getAllCategories() {
		return this.categoriesService.getCategories({ pageLimit: 500 }).pipe(
			tap(categories => {
				this.categories = this.formatDefaultCategoryName(categories.result);
				this.filterCategoriesAndSub();
			})
		);
	}

	filterCategoriesAndSub() {
		this.onlyCategories = this.categories.filter(
			(category: ICategory) => category.parentId === null
		);
		this.onlySubcategories = this.categories.filter(
			(category: ICategory) => category.parentId !== null
		);
	}

	getCategoryById(id: ICategory['id']) {
		return this.categoriesService.getCategory(id);
	}

	newCategory(category: ICategory) {
		return this.categoriesService
			.newCategory(category)
			.pipe(tap(category => this.categoriesStore.updateCategory(category)));
	}

	updateCategory(id: ICategory['id'], category: ICategory) {
		return this.categoriesService
			.editCategory(id, category)
			.pipe(tap(category => this.categoriesStore.updateCategory(category)));
	}

	deleteCategory(category: ICategory) {
		return this.categoriesService
			.deleteCategory(category.id)
			.pipe(tap(category => this.categoriesStore.deleteCategory(category)));
	}

	activateCategory(category: ICategory) {
		return this.categoriesService
			.activate(category.id)
			.pipe(tap(category => this.categoriesStore.deleteCategory(category)));
	}

	filterCategories(filter: IGetCategoriesParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}

	private formatDefaultCategoryName(categories: any[]): any[] {
		return categories.map(cat => {
			if (cat.isDefault && cat.name.toLowerCase() === 'sem categoria') {
				return { ...cat, name: cat.name.toUpperCase() };
			}
			return cat;
		});
	}
}
